/// <reference types="vite/client" />
import type { ComponentType } from 'react';
import { ECRANS, audience, type Ecran, type RoleCode } from '../api/contrat.js';
import { ESPACES } from './roles.js';

/**
 * Chemins des écrans des maquettes et droits d’accès par rôle. Le contrat (`ECRANS`) fait foi pour la liste
 * et le rôle de chaque écran ; ce fichier y ajoute l’adresse, le registre des composants des lots d’écrans
 * (`apps/web/src/features/*`) et la règle d’accès.
 */
const CHEMINS: Readonly<Record<string, string>> = {
  D01: '/pilotage',
  D02: '/file-du-jour',
  D03: '/dossier',
  D04: '/dossier/arbre',
  D04b: '/dossier/arbre/dispositions',
  D04c: '/dossier/estimation',
  D05: '/fiche-heritier',
  D06: '/boite',
  D07: '/relances',
  D08: '/facturation',
  D09: '/documents',
  D10: '/administration',
  D15: '/dossier/titre',
  D16: '/accueil',
  D17: '/dossier/demarrage',
  D18: '/guide',
  M01: '/decisions',
  M02: '/appel',
  M03: '/fiche-rapide',
  M04: '/portail/progression',
  M05: '/portail/piece',
  M06: '/portail/paiement',
  M07: '/portail/confidentialite',
  X11: '/espace-clerc',
  X12: '/espace-notaire',
  X13: '/portail/mandant',
  X14: '/portail/heritier',
  X15: '/portail/arbre',
};

const LIBELLES_NAVIGATION: Readonly<Record<string, string>> = {
  D01: 'Pilotage',
  D02: 'File du jour',
  D03: 'Dossiers',
  D04: 'Arbre et tableaux',
  D04b: 'Dispositions',
  D04c: 'Estimation des parts',
  D05: 'Fiche héritier',
  D06: 'Boîte unifiée',
  D07: 'Relances',
  D08: 'Facturation',
  D09: 'Documents',
  D10: 'Administration',
  D15: 'Fiche titre',
  D16: 'Entretien d’accueil',
  D17: 'Démarrage guidé',
  D18: 'Mode guidé',
  M01: 'Décisions',
  M02: 'Appel en cours',
  M03: 'Fiche rapide',
  M04: 'Ma progression',
  M05: 'Envoyer une pièce',
  M06: 'Régler ma quote-part',
  M07: 'Confidentialité',
  X11: 'Espace clerc',
  X12: 'Espace notaire',
  X13: 'Mon dossier',
  X14: 'Mon dossier',
  X15: 'Mon arbre',
};

export const libelleNavigation = (id: string): string => LIBELLES_NAVIGATION[id] ?? id;

export function cheminEcran(id: string): string {
  const chemin = CHEMINS[id];
  if (!chemin) throw new Error(`Aucun chemin pour l’écran ${id}`);
  return chemin;
}

const PAR_CHEMIN = new Map(ECRANS.map((e) => [cheminEcran(e.id), e] as const));

export function ecranParChemin(chemin: string): Ecran | null {
  const net = chemin.length > 1 ? chemin.replace(/\/+$/, '') : chemin;
  return PAR_CHEMIN.get(net) ?? null;
}

/** Le représentant légal et le contact délégué voient ce que voit l’héritier qu’ils accompagnent. */
const EQUIVALENT: Partial<Readonly<Record<RoleCode, RoleCode>>> = { representant_legal: 'heritier', contact_delegue: 'heritier' };

const RESPONSABLES: readonly RoleCode[] = ['admin', 'dirigeante'];

export function peutOuvrir(role: RoleCode, ecranId: string): boolean {
  const ecran = ECRANS.find((e) => e.id === ecranId);
  if (!ecran) return false;
  const effectif = EQUIVALENT[role] ?? role;
  if (audience(ecran).includes(effectif)) return true;
  return RESPONSABLES.includes(effectif) && ESPACES[ecran.role].espace === 'equipe';
}

export const ecransAccessibles = (role: RoleCode): Ecran[] => ECRANS.filter((e) => peutOuvrir(role, e.id));

export type ComposantEcran = ComponentType<{ ecran: Ecran }>;
export type RegistreEcrans = Readonly<Record<string, ComposantEcran>>;

/**
 * Construit le registre depuis les modules `features/<lot>/index.ts`, chacun exportant `ecrans: { D02: Composant }`.
 * Un écran inconnu du contrat ou déclaré par deux lots arrête le démarrage : c’est une erreur de câblage.
 */
export function construireRegistre(
  modules: Readonly<Record<string, { ecrans?: Readonly<Record<string, ComposantEcran>> }>>,
): RegistreEcrans {
  const registre: Record<string, ComposantEcran> = {};
  for (const [fichier, module] of Object.entries(modules)) {
    for (const [id, composant] of Object.entries(module.ecrans ?? {})) {
      if (!ECRANS.some((e) => e.id === id)) throw new Error(`Écran ${id} inconnu du contrat (déclaré par ${fichier})`);
      if (registre[id]) throw new Error(`Écran ${id} déclaré deux fois (${fichier})`);
      registre[id] = composant;
    }
  }
  return registre;
}

export const REGISTRE: RegistreEcrans = construireRegistre(
  import.meta.glob<{ ecrans?: Record<string, ComposantEcran> }>('../features/*/index.{ts,tsx}', { eager: true }),
);
